/**
 * VAT Domestic Reverse Charge. When it applies, the 20% VAT is still calculated and shown
 * but withheld from the worker's pay — V Pay Tech accounts for it to HMRC as the customer
 * (VAT Act 1994 s55A). Everything here is Legend-only: the routes live under /admin/v1/drc.
 */

/** Only `not_end_user` makes the reverse charge eligible, and setting it stamps the profile. */
export enum END_USER_STATUSES {
  NOT_ASSESSED = 'not_assessed',
  END_USER = 'end_user',
  NOT_END_USER = 'not_end_user',
}

/** Recorded beside the DRC panel. Never an input to whether the reverse charge applies. */
export enum IR35_STATUSES {
  NOT_ASSESSED = 'not_assessed',
  OUTSIDE = 'outside',
  INSIDE = 'inside',
}

/** How a pay run line was calculated. Frozen once the run is submitted or paid. */
export enum VAT_TREATMENTS {
  NONE = 'none',
  STANDARD = 'standard',
  REVERSE_CHARGE = 'reverse_charge',
}

/**
 * Why the charge does not apply. `reason_code` is the headline; `blockers` is everything
 * currently failing, in a fixed order, and doubles as the "what to fix next" list.
 */
export enum DRC_REASON_CODES {
  NO_PROFILE = 'no_profile',
  NOT_VAT_REGISTERED = 'not_vat_registered',
  VAT_NOT_VERIFIED = 'vat_not_verified',
  NOT_CIS = 'not_cis',
  END_USER = 'end_user',
  END_USER_NOT_CONFIRMED = 'end_user_not_confirmed',
  NO_EFFECTIVE_DATE = 'no_effective_date',
  NOT_YET_EFFECTIVE = 'not_yet_effective',
  ENDED = 'ended',
}

export interface CompanyRegisteredOffice {
  address_line_1?: string | null;
  address_line_2?: string | null;
  locality?: string | null;
  region?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

export interface DRCProfile {
  vat_registered: boolean;
  vat_number: string | null;
  vat_registration_date: string | null;
  vat_scheme: string | null;
  cis_in_scope: boolean;

  end_user_status: END_USER_STATUSES;
  end_user_confirmed_by: string | null;
  /**
   * Comes back beside the id on every route that answers a profile — render it directly,
   * never look the name up again. Null only when the identity service could not name the
   * user, in which case fall back to the id.
   */
  end_user_confirmed_by_name: string | null;
  end_user_confirmed_at: string | null;

  ir35_status: IR35_STATUSES;
  ir35_determined_at: string | null;
  ir35_determined_by: string | null;
  cest_reference: string | null;
  cest_document_url: string | null;

  company_number: string | null;
  company_name: string | null;
  company_status: string | null;
  company_incorporated_on: string | null;
  company_registered_office: CompanyRegisteredOffice | null;
  company_verified_at: string | null;

  /** Both ends are inclusive: the charge applies on effective_from and on ended_on themselves. */
  drc_effective_from: string | null;
  drc_ended_on: string | null;
}

export interface DRCStatus {
  /** Derived per request from `as_of`, never stored. */
  applies: boolean;
  effective_from: string | null;
  reason_code: DRC_REASON_CODES | null;
  blockers: DRC_REASON_CODES[];
}

export interface DRCEvent {
  uuid: string;
  user_id: string;
  event_type: string;
  /** `{field: {old, new}}`, and legitimately empty — a re-affirmed ruling writes an empty event. */
  changes: Record<string, { old: unknown; new: unknown }>;
  actor_id: string | null;
  /**
   * The name as it stood on the day, frozen into the row. Print as given and never re-resolve
   * it from actor_id, or an old decision starts showing a new name. Null on pre-release rows
   * and on anything the platform did itself (actor_type 'service').
   */
  actor_name: string | null;
  actor_type: 'user' | 'service';
  note: string | null;
  created_at: string;
}

/** Every DRC mutation answers this shape. `profile` is null for a worker nobody has set up. */
export interface DRCResponse {
  profile: DRCProfile | null;
  status: DRCStatus;
  as_of: string;
  /** Null when the save changed nothing. */
  event: DRCEvent | null;
}

export interface DRCProfilePayload {
  vat_registered?: boolean;
  vat_number?: string | null;
  vat_registration_date?: string | null;
  vat_scheme?: string | null;
  cis_in_scope?: boolean;
  company_number?: string | null;
}

export interface DRCIR35Payload {
  status: IR35_STATUSES;
  determined_at?: string | null;
  cest_reference?: string | null;
  cest_document_url?: string | null;
}

export interface DRCPeriodPayload {
  effective_from?: string | null;
  ended_on?: string | null;
}

export interface DRCUsersSearchPayload {
  user_ids?: string[] | null;
  vat_registered?: boolean;
  cis_in_scope?: boolean;
  end_user_status?: END_USER_STATUSES;
  vat_verified?: boolean;
  has_effective_from?: boolean;
  on_date?: string;
  offset?: number;
  /** Capped at 100 by the server. */
  limit?: number;
}

export interface DRCUserRow {
  profile: DRCProfile;
  status: DRCStatus;
}

export interface DRCReportPayload {
  date_from: string;
  date_to: string;
  company_id?: string | null;
  /** Name or reference. */
  substring?: string | null;
  offset?: number;
  limit?: number;
}

export interface DRCReportRow {
  first_name: string;
  last_name: string;
  entity: string;
  vat_number: string;
  /**
   * Built in one place on the server so the screen, the CSV and the filed PDF cannot make
   * different claims about which weeks the VAT covers. Print it verbatim.
   */
  periods_label: string;
  /** Null for a worker charged at more than one rate. */
  vat_rate: string | null;
  drc_vat_paid: string;
  drc_vat_unpaid: string;
  drc_vat_total: string;
  pay_run_count: number;
}

export interface DRCReportTotals {
  /** The pair pre-summed — use this for a column rather than adding the halves yourself. */
  total_drc_vat: string;
  total_paid: string;
  total_unpaid: string;
  worker_count: number;
  pay_run_count: number;
  company_count: number;
}

export interface DRCReport {
  date_from: string;
  date_to: string;
  /** Covers the whole period, not the page. */
  totals: DRCReportTotals;
  count: number;
  items: DRCReportRow[];
}

export type DRCExportFormat = 'csv' | 'pdf';

export interface DRCReportExportPayload extends Omit<DRCReportPayload, 'offset' | 'limit'> {
  format: DRCExportFormat;
}
